/**
 * Search data — DESIGN.md §12.4.
 *
 * One hook serves both the search page and the ⌘K palette, because §12.4 is
 * explicit that they share a syntax — and sharing the query means they cannot
 * drift into interpreting the same string differently.
 *
 * KEYS-17: typing "assignee:me is:blocked" in the palette fired one request
 * per character. `useDebouncedValue` holds the query still for a beat so the
 * palette asks once, when the typing stops.
 */
import { useEffect, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SearchResults } from '@slate/shared';
import { http } from '@/lib/api';

/**
 * The value, once it has stopped changing for `delay` ms. Clearing the field
 * is applied immediately: an empty query means "show me the default list", and
 * waiting to show that is just lag.
 */
export function useDebouncedValue<T>(value: T, delay = 200): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (value === '' || value === undefined || value === null) {
      setDebounced(value);
      return;
    }
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export function useSearch(
  query: string,
  options: { enabled?: boolean; limit?: number } = {},
): UseQueryResult<SearchResults, Error> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['search', trimmed, options.limit ?? 10] as const,
    queryFn: () =>
      http.get<SearchResults>('/search', { query: { q: trimmed, limit: options.limit ?? 10 } }),
    enabled: (options.enabled ?? true) && trimmed.length > 0,
    // Search is read-only and cheap to repeat; a short cache keeps the palette
    // instant while arrowing through results without going stale on the page.
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}
